'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { TagsWelcomeModal } from './TagsWelcomeModal';
import { TagsCompleteModal } from './TagsCompleteModal';
import { TAGS_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = TAGS_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * TagsTourOverlay - Renders the Tags tour step UI.
 *
 * Only renders when the active tour is 'tags'. Handles navigation
 * to Settings for tag creation and to Voley Support for tag assignment.
 */
export function TagsTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isTagsTour = activeTourId === 'tags';

  // Auto-advance polling
  useTourAutoAdvance(isTagsTour, currentStep, nextStep);

  // Find the Voley Support conversation for navigation
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isTagsTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportId = searchResults?.items?.[0]?.id;

  // Track which steps we've already navigated for
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation explicitly in the overlay
  useEffect(() => {
    if (!isTagsTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Navigate to Voley Support for the contact panel step
    if (currentStep.id === 'highlight_contact_panel_btn' && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info('[TagsTour] Navigating to Voley Support for tag assignment');
      return;
    }

    // Navigate to All Conversations for the filter step
    if (currentStep.id === 'highlight_filter_btn') {
      navigatedForStepRef.current = currentStep.id;
      router.push('/dashboard?view=all');
      reduxLogger.info('[TagsTour] Navigating to All Conversations for filtering');
    }
  }, [isTagsTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    router.push('/settings');
    reduxLogger.info('[TagsTour] Navigating to Settings');
    nextStep();
  }, [router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('tags');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isTagsTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'tags_welcome' && <TagsWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'tags_complete' && <TagsCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
